<template>
  <section :class="['status', `status--${state}`]" aria-live="polite">
    <button class="status__head" type="button" @click="expanded = !expanded">
      <span class="status__dot" aria-hidden="true"></span>
      <span class="status__title">
        <strong>{{ headline }}</strong>
        <span class="status__text">{{ subline }}</span>
      </span>
      <span class="status__toggle">{{ expanded ? 'Скрыть' : 'Подробнее' }}</span>
    </button>

    <ul v-if="expanded" class="status__checks">
      <li v-for="check in checks" :key="check.key" :class="['check', `check--${check.state}`]">
        <span class="check__dot" aria-hidden="true"></span>
        <div>
          <div class="check__title">{{ check.title }} — {{ stateLabel(check.state) }}</div>
          <div class="check__text">{{ check.text }}</div>
          <div v-if="check.action" class="check__action">Что делать: {{ check.action }}</div>
        </div>
      </li>
      <li v-if="!checks.length" class="check check--degraded">
        <span class="check__dot" aria-hidden="true"></span>
        <div class="check__text">Не удалось получить состояние системы. Сервер ERP может быть недоступен.</div>
      </li>
    </ul>

    <div class="status__legend">
      <span class="lg lg--ok"><i></i><b>Зелёный</b> — всё работает</span>
      <span class="lg lg--degraded"><i></i><b>Жёлтый</b> — есть задержки, система догоняет сама; работайте как обычно</span>
      <span class="lg lg--down"><i></i><b>Красный</b> — сбой: часть данных не поступает; смотрите «Что делать» в строке проверки, IT уже уведомлён</span>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { STATE_LABELS } from '@/pages/workspace/workspaceLogic.mjs'

const props = defineProps({
  health: { type: Object, default: null },
  error: { type: String, default: '' },
  loading: { type: Boolean, default: false },
})

const expanded = ref(false)
const checks = computed(() => props.health?.checks || [])
const state = computed(() => {
  if (props.error) return 'down'
  return props.health?.overall?.state || 'unknown'
})
// Если что-то не в порядке — подробности открыты сразу: менеджер видит, что случилось и что делать.
watch(state, (value) => {
  if (value === 'down' || value === 'degraded') expanded.value = true
}, { immediate: true })
const headline = computed(() => {
  if (props.loading && !props.health) return 'Проверяем систему…'
  if (props.error) return 'Сервер ERP не отвечает'
  return `Система: ${STATE_LABELS[state.value] || 'нет данных'}`
})
const subline = computed(() => {
  if (props.error) return 'Это сбой на стороне системы, а не ваша ошибка. Нажмите «Сообщить в IT».'
  return props.health?.overall?.text || ''
})

function stateLabel(value) {
  return STATE_LABELS[value] || value
}
</script>

<style scoped>
.status {
  --c: #9ca3af;
  --bg: #f9fafb;
  border: 1px solid #e5e7eb;
  border-left: 4px solid var(--c);
  border-radius: 12px;
  background: var(--bg);
}
.status--ok { --c: #16a34a; --bg: #f0fdf4; }
.status--degraded { --c: #d97706; --bg: #fffbeb; }
.status--down { --c: #dc2626; --bg: #fef2f2; }

.status__head {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: #111827;
}
.status__dot, .check__dot {
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--c);
}
.status__title { flex: 1; display: flex; flex-direction: column; gap: 2px; }
.status__text { font-size: 0.85rem; color: #4b5563; }
.status__toggle { font-size: 0.8rem; color: #4f46e5; white-space: nowrap; }

.status__checks { list-style: none; margin: 0; padding: 0 16px 14px 40px; display: grid; gap: 10px; }
.check { --c: #9ca3af; display: flex; gap: 10px; align-items: flex-start; }
.check--ok { --c: #16a34a; }
.check--degraded { --c: #d97706; }
.check--down { --c: #dc2626; }
.check__dot { width: 9px; height: 9px; margin-top: 6px; }
.check__title { font-weight: 600; font-size: 0.875rem; color: #111827; }
.check__text, .check__action { font-size: 0.85rem; color: #4b5563; line-height: 1.4; }
.check__action { color: #92400e; }

.status__legend { display: flex; flex-wrap: wrap; gap: 4px 16px; padding: 0 16px 12px 40px; font-size: 0.75rem; color: #6b7280; }
.lg { display: inline-flex; align-items: center; gap: 6px; }
.lg i { width: 8px; height: 8px; border-radius: 50%; background: #9ca3af; }
.lg b { color: #374151; font-weight: 600; }
.lg--ok i { background: #16a34a; }
.lg--degraded i { background: #d97706; }
.lg--down i { background: #dc2626; }
</style>
